import { useEffect, useMemo, useRef, useState } from "react";

import { ParticipantDetail, ParticipantStatus } from "@asd/customer-portal-api";
import { useOrganization } from "@asd/customer-portal-ui";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useKeycloak } from "~/components/authorization";
import { isUserCaseManager } from "~/utils/keycloakHelpers";
import { useDashboardStaffProvider } from "./DashboardStaffProvider";
import ParticipantSelectionList from "./ParticipantSelectionList";

export interface ParticipantSelectionDetailsProps {
  sortedParticipantDetails: ParticipantDetail[];
  pendingSelectedPPT: ParticipantDetail | null;
  setPendingSelectedPPT: (_participant: ParticipantDetail | null) => void;
  trimmedSearchTerm: string;
  userIsCaseManager: boolean;
}

const ParticipantSelection = () => {
  const theme = useTheme();
  const {
    configuration: { participantIdentifier, supportCoordinator },
  } = useOrganization();

  const keycloak = useKeycloak();
  const { participantDetails, error, isLoading, selectedParticipant, setSelectedParticipant } = useDashboardStaffProvider();

  const navigate = useNavigate();

  const activeParticipantDetails = useMemo(
    () => (participantDetails ?? []).filter((pd) => pd.status === ParticipantStatus.Active),
    [participantDetails],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [trimmedSearchTerm, setTrimmedSearchTerm] = useState("");
  const [filteredParticipantDetails, setFilteredParticipantDetails] = useState<ParticipantDetail[]>(activeParticipantDetails);
  const [showInactive, setShowInactive] = useState(false);
  const [pendingSelectedPPT, setPendingSelectedPPT] = useState<ParticipantDetail | null>(selectedParticipant);

  const filterDebounceTimer = useRef(null as NodeJS.Timeout | null);

  const userIsCaseManager = !!isUserCaseManager(keycloak);

  const sortedParticipantDetails = useMemo(
    () =>
      [...filteredParticipantDetails].sort((a, b) => {
        let caseManagerSort = 0;

        if (!userIsCaseManager) {
          const caseManagerA = a.caseManagerName?.toLowerCase() ?? "";
          const caseManagerB = b.caseManagerName?.toLowerCase() ?? "";

          if (caseManagerA < caseManagerB) {
            caseManagerSort = -1;
          } else if (caseManagerA > caseManagerB) {
            caseManagerSort = 1;
          }
        }

        if (caseManagerSort !== 0) {
          return caseManagerSort;
        }

        const nameA = `${a.firstName?.toLowerCase()} ${a.lastName?.toLowerCase()}`;
        const nameB = `${b.firstName?.toLowerCase()} ${b.lastName?.toLowerCase()}`;

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        return 0;
      }),
    [filteredParticipantDetails, userIsCaseManager],
  );

  const participantSelectionList = useMemo(
    () => (
      <ParticipantSelectionList
        pendingSelectedPPT={pendingSelectedPPT}
        setPendingSelectedPPT={setPendingSelectedPPT}
        sortedParticipantDetails={sortedParticipantDetails}
        trimmedSearchTerm={trimmedSearchTerm}
        userIsCaseManager={userIsCaseManager}
      />
    ),
    [pendingSelectedPPT, sortedParticipantDetails, trimmedSearchTerm, userIsCaseManager],
  );

  useEffect(() => {
    const filteredDetails = ((showInactive ? participantDetails : activeParticipantDetails) ?? []).filter((pd) => {
      const caseManagerName = pd.caseManagerName?.toLowerCase().replace(/\s/g, "");
      const fullName = `${pd.firstName}${pd.lastName}`.toLowerCase().replace(/\s/g, "");
      return (
        (!userIsCaseManager && caseManagerName?.includes(trimmedSearchTerm)) ||
        fullName.includes(trimmedSearchTerm) ||
        pd[participantIdentifier.key]?.includes(trimmedSearchTerm)
      );
    });
    setTrimmedSearchTerm(trimmedSearchTerm);
    setFilteredParticipantDetails(filteredDetails);
  }, [activeParticipantDetails, participantDetails, showInactive, trimmedSearchTerm, userIsCaseManager, participantIdentifier]);

  return (
    <Stack alignItems="center" direction="column" flexGrow={1} height="100%" justifyContent="center" padding={2} width="100%">
      <Card elevation={6} sx={{ padding: 4, maxWidth: "lg", width: "100%", height: "100%" }}>
        <Stack alignItems="center" direction="column" height="100%" position="relative" spacing={3}>
          <Typography align="center" color="custom.main" fontWeight="bold" variant="h4" gutterBottom>
            Select a Participant/Member
          </Typography>
          {isLoading || !participantDetails?.length || error ? (
            <Stack direction="column" overflow="hidden" position="absolute" sx={{ inset: 0, top: theme.spacing(4) }}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  alignContent: "center",
                  textAlign: "center",
                  background: theme.palette.background.paper,
                  zIndex: theme.zIndex.drawer - 1,
                }}
              >
                {isLoading && <CircularProgress size={150} />}
                <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                  {error ? error : isLoading ? "Loading..." : "No Participants/Members Found"}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <>
              <Stack direction="row" width="100%">
                <TextField
                  onChange={(e) => {
                    setSearchTerm(e.target.value);

                    if (filterDebounceTimer.current) {
                      clearTimeout(filterDebounceTimer.current);
                    }

                    filterDebounceTimer.current = setTimeout(() => {
                      setTrimmedSearchTerm(e.target.value.toLowerCase().replace(/\s/g, ""));
                    }, 500);
                  }}
                  placeholder="Search Participants/Members"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start" sx={{ marginLeft: 2 }}>
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="start" sx={{ marginRight: 2 }}>
                          <Tooltip title="Clear Search">
                            <IconButton
                              onClick={() => {
                                setSearchTerm("");
                                setTrimmedSearchTerm("");
                                setFilteredParticipantDetails(showInactive ? participantDetails : activeParticipantDetails);
                              }}
                              size="small"
                              sx={{
                                visibility: searchTerm ? "visible" : "hidden",
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 20,
                        boxShadow: 2,
                      },
                    },
                  }}
                  sx={{ minWidth: theme.spacing(20) }}
                  value={searchTerm}
                  variant="outlined"
                  fullWidth
                />
                <Divider orientation="vertical" sx={{ ml: 2 }} flexItem />
                <FormControlLabel
                  control={<Checkbox checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} size="small" />}
                  label={<Typography>Show Inactive</Typography>}
                  sx={{ mt: 0, textWrap: "nowrap" }}
                />
              </Stack>
              <Box display="grid" flexGrow={1} gap={2} gridTemplateColumns="1fr 1fr" overflow="auto" p={0.5} width="100%">
                <Card elevation={2} sx={{ flexGrow: 1, height: "100%" }}>
                  <Stack alignItems="center" direction="column" height="100%" overflow="auto" p={2}>
                    {!filteredParticipantDetails.length ? (
                      <Box alignContent="center" height="100%">
                        <Typography align="center">No Results Found</Typography>
                      </Box>
                    ) : (
                      participantSelectionList
                    )}
                  </Stack>
                </Card>
                <Card elevation={2} sx={{ flexGrow: 1, height: "100%" }}>
                  <Stack alignItems="center" direction="column" height="100%" overflow="auto" px={2} py={6} spacing={4}>
                    <Typography align="center" color="custom.main" fontWeight="bold" variant="h5" gutterBottom>
                      Selection
                    </Typography>
                    <Stack alignItems="center" direction="column" flexGrow={1} justifyContent="center" spacing={2}>
                      <Typography
                        align="center"
                        alignContent="center"
                        fontWeight="bold"
                        sx={{
                          fontStyle: pendingSelectedPPT ? "normal" : "italic",
                        }}
                        variant="h4"
                        gutterBottom
                      >
                        {!pendingSelectedPPT
                          ? "(No Participant/Member Selected)"
                          : `${pendingSelectedPPT.firstName} ${pendingSelectedPPT.lastName}`}
                      </Typography>
                      {!!pendingSelectedPPT && (
                        <Typography align="center" color="text.secondary" variant="h6">
                          {participantIdentifier.longLabel}: {pendingSelectedPPT[participantIdentifier.key]}
                        </Typography>
                      )}
                      {supportCoordinator.enabled && !!pendingSelectedPPT && !userIsCaseManager && (
                        <Typography align="center" color="text.secondary" variant="h6">
                          {supportCoordinator.title}: {pendingSelectedPPT.caseManagerName}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={4}>
                      <Button
                        color="inherit"
                        disabled={!pendingSelectedPPT}
                        onClick={() => {
                          setPendingSelectedPPT(null);
                          setSelectedParticipant(null);
                        }}
                        variant="contained"
                      >
                        Clear
                      </Button>
                      <Button
                        disabled={!pendingSelectedPPT}
                        onClick={() => {
                          setSelectedParticipant(pendingSelectedPPT);
                          navigate("/member");
                        }}
                        variant="contained"
                      >
                        Confirm
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              </Box>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default ParticipantSelection;
